import React from 'react';
import ColorPicker from 'core/colorPicker';
import { noop } from 'utils/noop';
import { Delete } from 'ui/icons/action/Delete';
import { GhostButton, BUTTON_SIZES } from 'ui/button';
import {
  ColorPickerContainer,
  MinMaxLabel,
  StyledCoreNumberInput,
  ValueBoxContainer,
} from './ValueBox.styled';

// type Props = {
//   /** Color of current range. */
//   color: string,
//   /** Disables decrementing the value. */
//   disableDecrement: boolean,
//   /** Disables incrementing the value. */
//   disableIncrement: boolean,
//   /** If the current value if the minimum value. */
//   isMin: boolean,
//   /** If the current value if the maximum value. */
//   isMax: boolean,
//   /** The value's index. */
//   index: number,
//   minValue: number,
//   maxValue: number,
//   /**
//    * Callback function when color value changes.
//    *
//    * ```
//    * onChangeColor(e, colorProps)
//    * ```
//    * @param { Object } e: The change event.
//    * @param { Array } colorProps: Object containing new color and value's index.
//    * @returns { void }
//    */
//   onChangeColor: (e: {}, colorProps: { index: number, color: string }) => void,
//   /**
//    * Callback function when value changes.
//    *
//    * ```
//    * onChangeValue(e, valueProps)
//    * ```
//    * @param { Object } e: The change event.
//    * @param { Array } valueProps: Object containing new value and value's index.
//    * @returns { void }
//    */
//   onChangeValue: (e: {}, valueProps: { index: number, value: number }) => void,
//   /**
//    * Callback function when minimum value changes.
//    *
//    * ```
//    * onChangeMin(e, min)
//    * ```
//    * @param { Object } e: The change event.
//    * @param { Number } min: The new min value.
//    * @returns { void }
//    */
//   onChangeMin: (e: {}, min) => void,
//   /**
//    * Callback function when maximum value changes.
//    *
//    * ```
//    * onChangeMax(e, min)
//    * ```
//    * @param { Object } e: The change event.
//    * @param { Number } max: The new max value.
//    * @returns { void }
//    */
//   onChangeMax: (e: {}, max) => void,
//   /**
//    * Callback function value is deleted.
//    *
//    * ```
//    * onDelete(e, min)
//    * ```
//    * @param { Object } e: The delete event.
//    * @param { Number } deleteProps: Props of the value box containing the index to delete.
//    * @returns { void }
//    */
//   onDelete: (e: {}, deleteProps: {}) => void,
//   /** Value of the current range. */
//   value: number,
// };

export default class ValueBox extends React.Component {
  static defaultProps = {
    isMin: false,
    isMax: false,
    onChangeColor: noop,
    onChangeMin: noop,
    onChangeMax: noop,
    onChangeValue: noop,
    onDelete: noop,
    value: 0,
  };

  constructor(props) {
    super(props);

    this.state = {
      value: props.value,
    };
  }

  /**
   * shouldComponentUpdate
   * Updates state value if prop value changes.
   * @param { Object } nextProps: The updated props.
   * @returns { Boolean } If component should update.
   */
  shouldComponentUpdate = (nextProps) => {
    const { value } = nextProps;
    if (this.props.value !== value) {
      this.setState({ value });
    }
    return true;
  };

  /**
   * handleChange
   * Callback function when value changes to trigger onChangeValue prop.
   * @param { Object } e: The change event.
   * @returns { void }
   */
  handleChange = (e) => {
    const {
      index,
      isMin,
      isMax,
      minValue,
      maxValue,
      onChangeMin,
      onChangeMax,
      onChangeValue,
      value: oldValue,
    } = this.props;
    const { value } = this.state;
    let changedValue = value;
    let canChange = false;

    if (isMin) {
      canChange = value <= maxValue;
      changedValue = canChange ? value : oldValue;
      onChangeMin(e, changedValue);
    }
    if (isMax) {
      canChange = value >= minValue;
      changedValue = canChange ? value : oldValue;
      onChangeMax(e, changedValue);
    }
    if (!canChange && (isMin || isMax)) {
      this.setState({ value: changedValue });
    }
    onChangeValue(e, { index, value: changedValue });
  };

  /**
   * handleChangeColor
   * Callback function when color changes.
   * @param { Object } e: The change event.
   * @param { Object } colorProps: Object containing new color value.
   * @returns { void }
   */
  handleChangeColor = (e, { value }) => {
    const { index, onChangeColor } = this.props;
    onChangeColor(e, { index, color: value });
  };

  /**
   * handleChangeValue
   * Callback function when value changes.
   * @param { Object } e: The change event.
   * @param { Object } valueProps: Object containing new value and if value
   *                               was changed from scrolling.
   * @returns { void }
   */
  handleChangeValue = (e, { value, isScrolled }) => {
    const newValue = parseFloat(value, 10) || 0;
    this.setState({ value: newValue });
    if (isScrolled) {
      this.handleChange(e);
    }
  };

  /**
   * handleClickDelete
   * Callback function when delete button is clicked.
   * @param { Object } e: The delete event.
   * @returns { void }
   */
  handleClickDelete = (e) => {
    this.props.onDelete(e, this.props);
  };

  render() {
    const { value } = this.state;
    const { color, disableDecrement, disableIncrement, isMin, isMax } = this.props;

    return (
      <ValueBoxContainer>
        <StyledCoreNumberInput
          disableIncrement={disableIncrement}
          disableDecrement={disableDecrement}
          onBlur={this.handleChange}
          onChange={this.handleChangeValue}
          value={value}
        />
        <ColorPickerContainer>
          <ColorPicker onChange={this.handleChangeColor} value={color} />
          {!isMin && !isMax ? (
            <GhostButton size={BUTTON_SIZES.SMALL} onClick={this.handleClickDelete}>
              <Delete height={18} width={18} />
            </GhostButton>
          ) : (
            <MinMaxLabel>{isMin ? 'Min' : 'Max'}</MinMaxLabel>
          )}
        </ColorPickerContainer>
      </ValueBoxContainer>
    );
  }
}
